import { restApi } from '../../api/restApiSlice';
import { CfEntrySource, CfSubCase } from './CfProbeResponse';

export type CfFlowScreen =
  | 'blocked'
  | 'cf_account'
  | 'token'
  | 'reconnect'
  | 'nameservers'
  | 'propagation'
  | 'progress'
  | 'error'
  | 'success'
  | 'abandoned';

/**
 * Mirrors `CF_FLOW_STATUSES` in rest-api's `CfFlowState`. A bare `string` here
 * let a screen decide a variant from a status the server never sends, and let a
 * component be handed a status it has no copy for.
 */
export type CfFlowStatus =
  | 'blocked'
  | 'complete'
  | 'abandoned'
  | 'awaiting_cf_account'
  | 'awaiting_token'
  | 'awaiting_nameservers'
  | 'awaiting_propagation'
  | 'propagation_timed_out'
  | 'preflight_failed'
  | 'deploy_failed'
  | 'verification_failed'
  | 'token_connected'
  | 'preflight_running'
  | 'deploying'
  | 'verifying';

export type CfRollbackOutcome = 'not_needed' | 'succeeded' | 'failed';

/**
 * Mirrors `CfTokenInvalidReason` in rest-api. `deleted_by_retention` means
 * Prerender deleted the stored token under the retention rule and the customer
 * did nothing; the reconnect screen must say so.
 */
export type CfReconnectReason = 'revoked' | 'deleted_by_retention';

export type CfFlowStep = {
  state: 'pending' | 'running' | 'passed' | 'failed' | 'skipped' | 'rolled_back';
  attempts?: number;
  error_code?: number;
};

export type CfFlowResponse = {
  screen: CfFlowScreen;
  status: CfFlowStatus;
  polls: boolean;
  reconnectReason: CfReconnectReason | null;
  tokenRetentionDays: number;
  attemptNumber?: number;
  /** Optional: a dashboard build can reach a browser ahead of the rest-api that sends it. */
  tokenTemplateUrl?: string;
  domain: string;
  subCase: CfSubCase;
  entrySource: CfEntrySource;
  steps: Record<string, CfFlowStep>;
  rollbackOutcome: CfRollbackOutcome | null;
  errorDetail: {
    stage?: string;
    /** Not a mirrored union: a reason this build does not know must still arrive and fall back. */
    reason?: string;
    leftovers?: readonly string[];
    nexusError?: string;
  } | null;
  nameservers: string[] | null;
  lastCheckedAt: string | null;
  pendingSince: string | null;
  createdAt: string;
  updatedAt: string;
};

const cloudflareFlowApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getCfFlow: builder.query<CfFlowResponse, { domain: string }>({
      query: ({ domain }) => ({
        url: '/v3/cloudflare/flow',
        method: 'GET',
        params: { domain },
      }),
    }),
  }),
});

export { cloudflareFlowApi };
export const { useGetCfFlowQuery } = cloudflareFlowApi;
